import { useMemo, useState } from 'react'

import { create } from '@bufbuild/protobuf'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { timestampDate } from '@bufbuild/protobuf/wkt'
import { BidSegment } from '@ebalance/proto/bfftypes/v1/bid_segment_pb'
import {
  BiddingApprovalState,
  BiddingLifecycleAction,
} from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import {
  BiddingIntradayOrderOrderMethod,
  BiddingIntradayOrderWithdrawState,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_model_pb'
import type { UpdateBiddingIntradayOrderRequest } from '@ebalance/proto/bffuser/v1/bidding_intraday_service_pb'
import { DateSchema } from '@ebalance/proto/google/type/date_pb'
import { zodResolver } from '@hookform/resolvers/zod'
import type { TFunction } from 'i18next'
import { type FieldErrors, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Box,
  HStack,
  OutlineDangerButton,
  PrimaryButton,
  Separator,
  TertiaryButton,
  Text,
  TextButton,
  VStack,
} from '@enechain/ecloud-designsystem'

import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { Modal } from '~/components/common/Modal'
import { RegionLoading } from '~/components/common/RegionLoading'
import { TradeSideLabel } from '~/components/common/TradeSideLabel'
import { useBidLimits } from '~/hooks/bidding/useBidLimits'
import type { IntradayOrderRow } from '~/hooks/bidding/useListBiddingIntradayOrders'
import { useTransitionBiddingIntradayOrderState } from '~/hooks/bidding/useTransitionBiddingIntradayOrderState'
import { useUpdateBiddingIntradayOrder } from '~/hooks/bidding/useUpdateBiddingIntradayOrder'
import { useMemberDisplayName } from '~/hooks/member/useMemberDisplayName'
import { useNowByMinute } from '~/hooks/useNowByMinute'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { resolveFormErrorAlertMessage } from '~/libs/formErrors'
import { now as serverNow } from '~/models/clock'
import {
  formatJstDateTimeHm,
  formatJstMonthDayHm,
  formatJstTimeHm,
  fromProtoDate,
} from '~/models/dateTime'
import {
  translateIntradayServerError,
  translateIntradayUpdateError,
} from '~/models/intradayServerError'
import { BiddingIntradayWithdrawStateTag } from '~/pages/IntradayBidding/components/BiddingIntradayWithdrawStateTag'
import { IntradayApprovalDialogs } from '~/pages/IntradayBidding/components/IntradayApprovalDialogs'
import { IntradayBidStatusCell } from '~/pages/IntradayBidding/components/IntradayBidStatusCell'
import { IntradayDeleteConfirmDialog } from '~/pages/IntradayBidding/components/IntradayDeleteConfirmDialog'
import { IntradayOrderEditForm } from '~/pages/IntradayBidding/components/IntradayOrderEditForm'
import {
  IntradayStrategyScheduleEditor,
  IntradayStrategyScheduleReadOnly,
} from '~/pages/IntradayBidding/components/IntradayStrategyScheduleEditor'
import { useGetBiddingIntradayOrder } from '~/pages/IntradayBidding/hooks/useGetBiddingIntradayOrder'
import { useIntradayApprovalDecision } from '~/pages/IntradayBidding/hooks/useIntradayApprovalDecision'
import { useIntradayApproverContext } from '~/pages/IntradayBidding/hooks/useIntradayApproverContext'
import { useIntradayDeleteConfirm } from '~/pages/IntradayBidding/hooks/useIntradayDeleteConfirm'
import type { IntradayFeeStateHandle } from '~/pages/IntradayBidding/hooks/useIntradayFeeState'
import { useNotifyWithdrawAccepted } from '~/pages/IntradayBidding/hooks/useIntradayWithdrawNotice'
import { resolveApprovalStageProgress } from '~/pages/IntradayBidding/models/approvalStageProgress'
import { convertIntradayBidFromOrder } from '~/pages/IntradayBidding/models/convertIntradayBidsFromOrders'
import { calcGcAtForKoma } from '~/pages/IntradayBidding/models/gcCountdown'
import { intradayApprovalDetailLine } from '~/pages/IntradayBidding/models/intradayApprovalDetailLine'
import { formulaSummary } from '~/pages/IntradayBidding/models/intradayFormula'
import {
  intradayGatePhase,
  intradayRowOperationGateBlockedReason,
  isIntradayDraftEditable,
} from '~/pages/IntradayBidding/models/intradayGateClose'
import { withIntradayStrategySchedule } from '~/pages/IntradayBidding/models/intradayOrderWrite'
import { composeIntradayTableStatus } from '~/pages/IntradayBidding/models/intradayTableStatus'
import { marketOpenAt } from '~/pages/IntradayBidding/models/marketOpen'
import {
  type IntradayRejectionNoticeKind,
  resolveIntradayRejectionNotice,
} from '~/pages/IntradayBidding/models/rejectionNotice'
import {
  type IntradayStrategyFormValues,
  buildIntradayStrategyFormSchema,
  toIntradayStrategyFormDefaults,
} from '~/pages/IntradayBidding/models/strategyScheduleForm'
import { withdrawFailureTextKeys } from '~/pages/IntradayBidding/models/withdrawFailureText'
import { formatWithdrawSummary } from '~/pages/IntradayBidding/models/withdrawSummary'

type Props = {
  row: IntradayOrderRow
  isOpen: boolean
  onClose: () => void
  /**
   * 対象と同じ受渡日の注文 (一覧 RPC の行)。 承認確認の売買逆転 (AC-BIN-097) の検知に使う
   * (`useIntradayApprovalDecision`)。 未指定なら売買逆転の警告は出さない。
   */
  orders?: readonly IntradayOrderRow[]
  /** 画面が開く前から取得している手数料自動考慮の状態 (編集を開いた時に読む、 AC-BIN-004b)。 */
  feeState: IntradayFeeStateHandle
}

/** 本文の編集フォームと footer の保存ボタンを結ぶ form id。 */
const EDIT_FORM_ID = 'intraday-order-edit-form'

function formatProtoDate(
  d: { year: number; month: number; day: number } | undefined,
): string {
  if (d === undefined) return '—'
  const mm = String(d.month).padStart(2, '0')
  const dd = String(d.day).padStart(2, '0')
  return `${d.year}/${mm}/${dd}`
}

/** proto Timestamp → 日時表示。 書式は AC-BIN-047 の既定 `yyyy/MM/dd HH:mm`。 */
function formatTimestamp(ts: Timestamp | undefined): string {
  if (ts === undefined) return '—'
  return formatJstDateTimeHm(timestampDate(ts))
}

/**
 * 時間前注文 詳細モーダル。
 *
 * 行単位の状態遷移を扱う: 申請取消 (AC-BIN-048)、 板取消 (AC-BIN-008、 intraday 専用で
 * spot では非表示)、 承認 / 差戻し (AC-BIN-095)。
 *
 * 承認 / 差戻しは以前「承認案件一覧画面 (admin/operation/approval/approval-tasks.md) の
 * 責務」 として本モーダルに置いていなかったが、 同画面が現時点で動作保証対象外のため
 * 2026-08-26 の PdM 判断 (spec intraday-bidding.md AC-BIN-095、 issue #6400) で
 * 入札画面内に操作口を置く方針へ変わった。 スポット入札 (AC-BSP-094 / issue #5802) と
 * 対称の扱い。
 *
 * 承認 / 差戻しの操作口は、 入札一覧の操作列と本モーダルの両方に置く (AC-BIN-095)。 どちらも
 * 確認モーダル・差戻しダイアログ・遷移・承認者の判定 (下記 AC-BIN-098) を同じ hook
 * (`useIntradayApprovalDecision` / `useIntradayApproverContext`) に通し、 押せるかは backend が
 * 一覧・詳細の RPC で返す `approvalActionable` で決まる。 本モーダルは詳細 RPC の値を優先した札を
 * hook に渡す。 一覧側の承認状態は一覧 RPC の `approval_state`
 * (`BiddingIntradayOrderListItem.approval_state`) を `convertIntradayBidsFromOrders`
 * がそのまま写している (以前は送信軸からの推定で上書きしており PENDING を出せなかった)。
 *
 * ## 未実装の AC (spot と同じ前提)
 *
 * - AC-BIN-098 (承認者判定) は**表示側 (常時警告 + 確認モーダルの再確認) のみ**実装済み
 *   (監査ログ条項は別項、 下記参照): 現在ユーザーの member_id
 *   (`GetCurrentSessionResponse.member_id`、 #6641) と、 詳細 RPC が返す今の承認段階の
 *   承認者 (`currentStageApproverMemberIds`、 承認申請時に写した段、 AC-BIN-095。
 *   `useIntradayApproverContext`) を突合し、 承認者非該当 / 自己承認 (`requested_by`
 *   突合) をそれぞれ常時警告 + 確認モーダルの警告で出す (ブロックしない、 09/04 決定)。
 *   入札一覧の操作列も一覧 RPC の同じ値で同じ判定をする。 途中経過の表示自体の follow-up は #6594。
 *   自己承認判定 (`requested_by` 突合) の本番経路は #8023 (merge 済み) で通った: BFF
 *   `TransitionBiddingIntradayOrderState` が eauth の member_id を `operated_by` に詰め、
 *   backend handler が `ActorBy: option.New(req.GetOperatedBy())` として渡し、
 *   `IntradayOrder.RequestApproval` が `requested_by` に保存する。 突合相手の
 *   `GetCurrentSessionResponse.member_id` も同じ eauth member_id なので値域が一致する。
 * - AC-BIN-098 の監査ログ: APPROVE / REJECT は BidOperationLog に積まれない
 *   (`BidLifecycleAction.OperationType()` が CANCEL / REOPEN / WITHDRAW しか
 *   操作種別に写像しないため。 AC-BIN-098 が求める「承認者非該当」「自己承認」の別を
 *   添えた監査ログ記録も同じ理由で未着手)。 操作者そのもの (`approved_by` /
 *   `requested_by`) は #8023 で記録されるようになったので、 残るのは操作種別 enum の
 *   拡張を含む監査行の follow-up (#7202、 spot / block も同じ穴)。
 * - AC-BIN-097 の売買逆転チェックは警告として出す (承認はブロックしない)。 検知は
 *   `useIntradayApprovalDecision` が、 呼び出し元から受け取った受渡日の注文 (`orders`) で行う。
 *   テナント設定の入札制限 (AC-BIN-015) は backend
 *   `ensureWithinIntradayBidLimits` が承認 (APPROVE) 時にも再検証しており (T-41)、
 *   承認確認モーダルはその再検証結果 (RPC 失敗時の `ErrorInfo.metadata`) から
 *   違反明細を組み立ててインライン表示する (= spot `useSpotApprovalDecision` の
 *   ような事前取得した設定値による client 側事前計算ではなく、 承認試行の結果から
 *   組み立てる。 intraday はこの画面で入札制限の設定値そのものを事前取得しておらず
 *   〈spot の `spotBidLimits` 相当が未移植〉、 T-41 の提出確認モーダルと同じ理由で
 *   RPC 失敗からの抽出方式を採る)。
 * - AC-BIN-099 の GC ゲート: 承認・差戻しを押せるかは backend の `approvalActionable`
 *   (承認待ちで GC 前) で決まり、 backend の `TransitionState` も GC 後の承認・差戻しを理由
 *   つき (`APPROVAL_GC_PASSED`) で拒否する。 本モーダルの分単位の GC 判定 (`isBlocked`) は、
 *   詳細を取得した後に GC を跨いだ時の押下を止める事前チェック (申請取消・再編集・取下と共通)。
 * - AC-BIN-099 の後段「下書き・承認待ち・差戻しのまま GC を迎えた入札は失効へ遷移する」:
 *   失効にした札は、 約定軸が失効、 承認ステータスが N/A (下書きと同じ値) になる
 *   (AC-BIN-051)。 差戻し理由・日時のデータは backend に残るが、 本モーダルは操作口を無効化し、
 *   差戻しの見出しも理由・日時も出さない (`resolveIntradayRejectionNotice`)。
 *   承認案件一覧もこの札を載せない。
 * - 差戻し (AC-BIN-049): 本モーダルは差戻し理由・日時の表示と、 「再編集して再申請」 で
 *   下書きに戻すところまでを担う。 戻した下書きを詳細モーダルから編集する導線は #8093。
 *   差戻しのまま GC を過ぎた札は、 再編集が AC-BIN-049c で拒否され、 GC で失効になる
 *   (AC-BIN-099)。
 * - 承認申請中 (PENDING) の作られ方: 時間前は AC-BIN-094 / #6650 の対応 (2026-09-16) で
 *   作成時の `approvalMode` を対象エリアの承認フロー設定から解決するようになったため
 *   (`models/intradayApprovalMode.ts`。 `intradayOrderWrite.ts` の AUTO 固定は撤去済み)、
 *   要承認エリアで作成すれば画面から PENDING に到達する。 spot / block は
 *   ハードコード値が APPROVAL なので結果的に PENDING になっており、 承認フロー設定を
 *   参照する配線はまだ入っていない。
 *
 * 数量は保持値そのものが コマ別 MW (issue #4285。 AC-BIN-007 の編集モーダルは入札量(MW)、
 * AC-BIN-043 も入札量は単発スナップショット値 = MW)。
 */
export const IntradayOrderDetailModal = ({
  row,
  isOpen,
  onClose,
  orders,
  feeState,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const deleteConfirm = useIntradayDeleteConfirm()
  const notifyWithdrawAccepted = useNotifyWithdrawAccepted()
  const resolveDisplayName = useMemberDisplayName()
  const {
    data,
    isLoading,
    error: fetchError,
  } = useGetBiddingIntradayOrder(row.id)
  const { mutateAsync: transitionAsync, isPending: isOwnTransitionPending } =
    useTransitionBiddingIntradayOrderState()
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  )
  // AC-008: 取下は破壊的操作なので、一覧の「取下」と同様に確認ダイアログを挟む
  // (行クリックで詳細を開いた場合に確認なしで取消できてしまう不整合を防ぐ)。
  const [showWithdrawConfirm, setShowWithdrawConfirm] = useState(false)
  // 申請取消 (AC-BIN-048)。 確認なしの即時実行だと承認案件一覧から即時消滅する
  // 破壊的操作が誤操作で走ってしまう (spec 改訂、 spot AC-BSP-055 と同型)。
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)

  const entity = data?.order
  // 板取消の進行状態 (AC-BIN-008b)。 詳細の取得前は一覧行の値で出す。
  const withdrawState = entity?.withdrawState ?? row.withdrawState
  const withdrawFailureReason =
    entity?.withdrawFailureReason ?? row.withdrawFailureReason

  // ── AC-BIN-007: 下書きの編集フォーム ────────────────────────────────
  // 編集できるのは GC 前の下書きのみ。 それ以外は同じフォームを非活性で表示し保存操作を
  // 出さない (spec: 入力欄をすべて非活性にし保存操作を出さない)。 対象コマ・GC締切・
  // エリア・発注主体区分・JEPX受渡契約コードは読み取り専用で表示する (AC-BIN-007)。
  // 売/買は入札量・入札価格と同じく編集できる (#8279 の PdM 判断)。 承認モードは現状値の
  // まま送信する。
  const { mutateAsync: updateAsync, isPending: isSaving } =
    useUpdateBiddingIntradayOrder()
  // 編集フォームが入札制限・手数料自動考慮の確認を待つ間と、 確認が必要な状態の間は保存を止める。
  const [isSaveBlocked, setIsSaveBlocked] = useState(false)

  const saveUpdate = async (
    request: UpdateBiddingIntradayOrderRequest,
  ): Promise<void> => {
    setErrorMessage(undefined)
    try {
      await updateAsync(request)
    } catch (err) {
      // AC-058: 3 画面統一の serverError 文言 (手数料自動考慮・フォーミュラの拒否は「保存」 の文言)。
      setErrorMessage(translateIntradayUpdateError(err, t))
      return
    }
    onClose()
    // 2026-08-28 デザイナー指摘「削除・追加・編集の成功時は全画面共通で toast」。
    showSuccessToast({ title: t('page:biddingIntraday.editSave.success') })
  }

  // 入札方式とスケジュール (AC-BIN-005b) は、 本文の編集フォームと同じ 1 回の保存で送る
  // (別々に保存すると、 片方の保存がもう片方の入力を捨てる)。 段の検証が通らなければ保存しない。
  const handleValidSave = async (
    request: UpdateBiddingIntradayOrderRequest,
  ): Promise<void> => {
    if (!isStrategyEditable || deliveryDate === undefined) {
      await saveUpdate(request)
      return
    }
    await strategyForm.handleSubmit(async (values) => {
      strategyForm.clearErrors('root.serverError')
      await saveUpdate(
        withIntradayStrategySchedule(request, {
          bidMode: values.bidMode,
          schedule: values.schedule,
          deliveryDate,
        }),
      )
    }, onStrategyInvalid)()
  }

  // ----- 入札方式 / 戦略モードのスケジュール (AC-BIN-005b) -----
  // 受渡日は一覧行 (= 常にある) から取る。 詳細の取得前でも schema を組めるように
  // するため (取得後の entity.targetDate と同じ値)。
  const { year, month, day } = row.targetDate ?? { year: 0, month: 0, day: 0 }
  // 依存は数値の年月日にする (= 一覧の再取得で行 object が作り直されても form を
  // 初期値へ戻さない。 `values` の参照が変わると RHF は form を reset する)。
  const deliveryDate = useMemo(
    () =>
      year === 0
        ? undefined
        : fromProtoDate(create(DateSchema, { year, month, day })),
    [year, month, day],
  )
  // AC-BIN-100: 各段の量を上限数量(1札) と照合する (作成モーダルと同じ入札制限の lookup)。
  // 取得中は lookup が空のまま (= 制限なし相当) で、 その間に保存すると事前チェックを
  // 素通りするので、 取得中は保存ボタンを操作不可にする。 取得失敗は許容する (事前チェックは
  // 入力補助で、 上限の拒否は backend が行う)。
  const { limits: bidLimits, isLoading: isBidLimitsLoading } = useBidLimits(
    BidSegment.INTRADAY,
  )
  const strategySchema = useMemo(
    () =>
      deliveryDate === undefined
        ? undefined
        : buildIntradayStrategyFormSchema({
            t,
            deliveryDate,
            koma: row.timeCode,
            limits: bidLimits,
            order: { areaCode: row.areaCode, direction: row.direction },
            violationMessage: (violation) =>
              violation === 'beforeMarketOpen'
                ? t('page:biddingIntraday.strategy.beforeMarketOpen', {
                    openAt: formatJstMonthDayHm(marketOpenAt(deliveryDate)),
                  })
                : t('page:biddingIntraday.strategy.atOrAfterGc', {
                    gcAt: formatJstTimeHm(
                      calcGcAtForKoma(deliveryDate, row.timeCode),
                    ),
                  }),
          }),
    [t, deliveryDate, row.timeCode, row.areaCode, row.direction, bidLimits],
  )
  // `values` は詳細の取得 (と保存後の再取得) のたびに form を保存済みの値へ戻す。
  const strategyDefaults = useMemo<IntradayStrategyFormValues>(
    () =>
      entity === undefined || deliveryDate === undefined
        ? { bidMode: 'normal', schedule: [] }
        : toIntradayStrategyFormDefaults(entity, deliveryDate),
    [entity, deliveryDate],
  )
  const strategyForm = useForm<IntradayStrategyFormValues>({
    resolver:
      strategySchema === undefined ? undefined : zodResolver(strategySchema),
    values: strategyDefaults,
  })
  /**
   * 承認申請中 (PENDING) の 3 操作 (承認 / 差戻し / 申請取消)・差戻し中 (REJECTED) の
   * 再編集して再申請・取下を止める理由。 操作可なら undefined を返す。
   *
   * 承認 / 差戻しは GC 到達で無効 (AC-BIN-099)、 申請取消・再編集も対象コマの GC 前限定で
   * GC 後は拒否される (AC-BIN-048 / AC-BIN-050、 再編集は AC-BIN-049c)。 取下 (板取消) も
   * GC 後は受け付けない (AC-BIN-008) ため、 5 操作とも同じ判定に載せる。
   * GC の段階を文言にするのは、 承認・差戻しの hook と同じ
   * `intradayRowOperationGateBlockedReason` (`gcAt` 無し・差戻し中の再編集の文言の分け方も同関数)。
   * 取下ボタンを出すかは backend の `withdrawable`、 承認・差戻しを押せるかは
   * `approvalActionable` で決まり、 ここは取得後に GC を跨いだ時の押下を止める。
   */
  const gcAt = entity?.gcAt ?? row.gcAt
  const isRejectedOrder =
    entity?.approvalState === BiddingApprovalState.REJECTED
  const blockedReasonAt = (at: Date): string | undefined =>
    intradayRowOperationGateBlockedReason(
      t,
      gcAt,
      at,
      isRejectedOrder ? 'reopen' : 'approval',
    )
  // AC-BIN-099 は State-driven (GC を過ぎている「間」 無効) なので、 描画時の
  // スナップショットでは足りない (モーダルを開いたまま GC を跨ぐと活性のまま残る)。
  // 同ページの他の GC 判定と同じく分単位で引き直す。
  const nowByMinute = useNowByMinute()
  const blockedReason = blockedReasonAt(nowByMinute)
  const isBlocked = blockedReason !== undefined
  // AC-BIN-007 / §市場フェーズ「GC 済みのコマの入札作成・編集は不可」: GC を過ぎた
  // 下書きは表示専用にする (backend も FailedPrecondition で拒否する)。 GC が
  // 判定できない時も fail-closed で止める。 分単位で引き直す (上の isBlocked と同じ)。
  const isDraft = entity?.approvalState === BiddingApprovalState.DRAFT
  const editBlockedReason = ((): string | undefined => {
    switch (intradayGatePhase(gcAt, nowByMinute)) {
      case 'unknown':
        return t('page:biddingIntraday.editSave.gcUnknown')
      case 'closed':
        return t('page:biddingIntraday.editSave.gcPassed')
      case 'open':
        return undefined
    }
  })()
  // 編集可否は一覧の行の編集ボタン (#10110) と同じ判定。 上の理由文言はその表示用。
  const isDraftEditable = isIntradayDraftEditable(
    entity?.approvalState,
    gcAt,
    nowByMinute,
  )
  // 題 (#11458) は詳細の取得前も一覧行の承認状態で決め、 取得後に題が切り替わらないようにする。
  const isTitleEditable = isIntradayDraftEditable(
    entity?.approvalState ?? row.approvalState,
    gcAt,
    nowByMinute,
  )

  // AC-BIN-003: 詳細 RPC の値を優先し、 取れない間は一覧行の値で埋める。
  // ワークフローを経由せずに作った入札は未設定 (表示しない)。
  const workflowId = entity?.workflowId ?? row.workflowId

  /**
   * 確定直前の GC 判定 (申請取消の precheck と、 承認 / 差戻しの hook の判定) の結果を
   * 下地の Alert に反映する。 同じ理由を warning Alert (導出側 `blockedReason`) と
   * error Alert で二重に出さない。 ただし導出側は `useNowByMinute` の分 tick で更新される
   * ので、 分境界を跨ぐ前に確定直前の判定 (`serverNow`) だけが GC 経過を検知する窓がある。
   * その窓では導出側がまだ何も出しておらず、 ここで出さないと押しても無反応になるため、
   * 導出側が沈黙している時だけ error Alert に出す。 止めなかった時は前回のエラーを消す。
   */
  const reflectGateCheck = (blockedNow: string | undefined): void => {
    setErrorMessage(blockedReason === undefined ? blockedNow : undefined)
  }

  /**
   * 承認判断に出す 1 札。 **entity (詳細 RPC) 優先で行値へフォールバックする** (申請者は
   * 詳細 RPC の値)。 コマ・エリア・売買だけ行固定にすると、 一覧行と詳細が食い違ったとき
   * 同じ画面の本文 (entity.direction を表示) とサマリで売買方向が別値になる。
   * 承認 / 差戻しを開く時にこの札を `useIntradayApprovalDecision` に渡すので、 確認モーダルの
   * サマリ・違反抽出の入札値・楽観ロックの基準 (`updatedAt`)・確定直前の
   * GC 判定の `gcAt` もこの値になる。
   */
  const approvalOrder: IntradayOrderRow = {
    ...row,
    timeCode: entity?.timeCode ?? row.timeCode,
    areaCode: entity?.areaCode ?? row.areaCode,
    direction: entity?.direction ?? row.direction,
    orderMethod: entity?.orderMethod ?? row.orderMethod,
    priceYenPerKwh: entity?.priceYenPerKwh ?? row.priceYenPerKwh,
    volumeMwh: entity?.volumeMwh ?? row.volumeMwh,
    gcAt,
    updatedAt: entity?.updatedAt ?? row.updatedAt,
    requestedBy: entity?.requestedBy,
    currentStageApproverMemberIds: entity?.currentStageApproverMemberIds,
    approvalActionable: entity?.approvalActionable ?? row.approvalActionable,
  }
  /** 申請取消の確認に出す申請内容サマリ (承認確認と同じ 1 行明細)。 */
  const approvalDetailLine = intradayApprovalDetailLine(t, approvalOrder)

  /**
   * 承認者判定 (AC-BIN-098) の常時警告 (承認者非該当 / 自己承認)。 現在ユーザーの member_id
   * (#6641 で追加) と、 詳細 RPC が返す今の承認段階の承認者 (承認申請時に写した段) ・申請者を
   * 突合する (`useIntradayApproverContext`、 入札一覧の操作列と同じ判定と文言)。 ブロックは
   * しない (= 押下は常に可能。 `isBlocked` (AC-BIN-099 の GC 判定) とは独立の注意喚起
   * レイヤー)。 警告は詳細を取得済み (`entity`) の承認待ちの札にしか出さない。 requested_by は
   * REQUEST 遷移時に backend が設定する申請者 actor (#8023、 経路の詳細は本 file 冒頭 JSDoc)。
   */
  const approverNotices =
    useIntradayApproverContext().persistentNotices(approvalOrder)
  /** 多段階承認 (2 段以上) の承認待ちの札が待っている段。 詳細 RPC の値から出す。 */
  const stageProgress = resolveApprovalStageProgress({
    approvalStageCount: entity?.approvalStageCount,
    approvedStageCount: entity?.approvedStageCount ?? 0,
    isPending: entity?.approvalState === BiddingApprovalState.PENDING,
  })

  // 承認 / 差戻し (AC-BIN-095)。 確認モーダル ・ 差戻しダイアログ ・ 遷移は一覧の操作列と
  // 同じ hook が持つ。 成功したら詳細モーダルごと閉じ、 確定直前に GC を跨いでいたら
  // 下地の Alert に理由を出す。
  const approvalDecision = useIntradayApprovalDecision({
    orders,
    onSucceeded: onClose,
    reportGateCheck: reflectGateCheck,
  })
  const isTransitioning =
    isOwnTransitionPending || approvalDecision.isTransitioning

  /**
   * 申請取消 / 取下の実行経路。 送信前ガード → 送信 → 成功 toast / 失敗表示までをここに
   * 集約し、 確認 UI の開閉だけを呼び出し元に残す (= 経路が分かれていると片方だけに修正が
   * 入って挙動がズレる)。
   *
   * `precheck` は確定直前の追加ガード。 導線の disabled だけでは、 確認 UI を開いた
   * まま GC を跨いだ場合に送信が飛んでしまう。
   */
  const runTransition = async (
    action: BiddingLifecycleAction,
    {
      successTitle,
      precheck,
    }: {
      successTitle: string
      precheck?: () => string | undefined
    },
  ): Promise<void> => {
    const blockedNow = precheck?.()
    reflectGateCheck(blockedNow)
    if (blockedNow !== undefined) return
    let response: Awaited<ReturnType<typeof transitionAsync>>
    try {
      response = await transitionAsync({
        id: row.id,
        action,
        // entity が fetch 済みの場合はそちらの updatedAt を優先
        expectedUpdatedAt: entity?.updatedAt ?? row.updatedAt,
      })
    } catch (err) {
      // AC-058: 3 画面統一の serverError 文言 (二重作成 / 状態不正の専用文言を含む)。
      setErrorMessage(translateIntradayServerError(err, t))
      return
    }
    onClose()
    // 2026-08-28 デザイナー指摘「削除・追加・編集の成功時は全画面共通で toast」。
    // 詳細モーダルからの操作は一覧行の「取下」 と違い、 これまで成功しても
    // モーダルが閉じるだけで結果が分からなかった。
    // AC-BIN-008b: 板取消が JEPX 失敗でリトライ中 (withdrawState=PENDING) なら
    // 「取消しました」ではなく処理中トーストを出す。
    if (action === BiddingLifecycleAction.WITHDRAW) {
      notifyWithdrawAccepted(row.id, response.order, successTitle)
    } else {
      showSuccessToast({ title: successTitle })
    }
  }

  /** 確定直前に GC を引き直す (= 分 tick 直前の 1 分未満のズレを止める最終防波堤)。 */
  const gcPrecheck = (): string | undefined => blockedReasonAt(serverNow())

  // 申請取消確認ダイアログの「申請取消」確定 → CANCEL (AC-BIN-048)。 対象入札の
  // サマリと「申請取消すると承認案件が消滅します」の警告は confirm モーダル
  // (SubmitConfirmModal、 下記 JSX) 側で出す。 失敗時は確認だけ閉じて下地の
  // error alert を見せる (= 取下 handleWithdrawConfirm と同じ、 再入力を要する
  // 内容が無いので開いたまま残さない)。
  const handleCancelConfirm = async (): Promise<void> => {
    await runTransition(BiddingLifecycleAction.CANCEL, {
      successTitle: t('page:biddingIntraday.cancelRequest.success'),
      precheck: gcPrecheck,
    })
    setShowCancelConfirm(false)
  }

  // 差戻し後再編集 (AC-BIN-049)。 spot の `handleReopenRequest` と同じ判断で確認 UI を
  // 挟まない (= 下書きに戻すだけの可逆操作)。 GC 経過後は backend が拒否する
  // (AC-BIN-049c) が、 UI 側でも他の操作口と同じ二重防御をかける。 成功時は
  // 「下書きに戻った」 事実だけを toast で伝える (編集導線は #8093)。
  const handleReopen = (): void => {
    void runTransition(BiddingLifecycleAction.REOPEN, {
      successTitle: t('page:biddingIntraday.approvalAction.reopenSuccess'),
      precheck: gcPrecheck,
    })
  }

  // 取消確認ダイアログの「取消」確定 → WITHDRAW。成功時は runTransition が
  // モーダル全体を閉じる。失敗時は確認だけ閉じて下地の error alert を見せる
  // (= 再入力を要する内容が無いので開いたまま残さない)。
  const handleWithdrawConfirm = async (): Promise<void> => {
    await runTransition(BiddingLifecycleAction.WITHDRAW, {
      successTitle: t('page:biddingIntraday.withdrawConfirm.success'),
    })
    setShowWithdrawConfirm(false)
  }

  const approvalState = entity?.approvalState
  const bidStatus = entity?.bidStatus ?? row.bidStatus
  const hasRejectionHistory =
    entity?.rejectedReason !== undefined || entity?.rejectedAt !== undefined
  const rejectionNotice = resolveIntradayRejectionNotice(
    approvalState,
    hasRejectionHistory,
    bidStatus,
  )
  // AC-BIN-092 / AC-BIN-093: 予約中 (市場未開場でまだ板に載っていない状態) は
  // bidStatus だけでは 入札中 (PLACED) と区別できない (RESERVED は送信軸
  // (sendStatus) だけが立つ、bidding-reserved-send 設計書 §4)。一覧のステータス
  // 列と同じ合成判定 (composeIntradayTableStatus) を再利用し、判定 SoT を割らない。
  const sendStatus = entity?.sendStatus ?? row.sendStatus
  const statusSource = {
    bidStatus,
    sendStatus,
    approvalState,
    withdrawable: (entity ?? row).withdrawable,
    withdrawState,
    orderMethod: (entity ?? row).orderMethod,
    needsReview: (entity ?? row).needsReview,
    jepxBidNo: (entity ?? row).jepxBidNo,
    sendErrorCategory: (entity ?? row).sendErrorCategory,
    sendErrorDetail: (entity ?? row).sendErrorDetail,
  }
  const isReserved = composeIntradayTableStatus(statusSource) === 'reserved'

  /**
   * 入札方式 / スケジュールを編集できるか (AC-BIN-007: 編集できるのは GC 前の下書きのみで、
   * それ以外は表示専用)。 本文の編集フォームと同じ条件。 backend の Update も同じ条件で
   * 拒否する (二重防御)。
   */
  const isStrategyEditable = isDraftEditable && deliveryDate !== undefined
  // 戦略を選んでいる間は、 本文の価格を固定価格だけにする (本文の編集フォームが欄と送る値を切り替える)。
  const isStrategyMode =
    isStrategyEditable && strategyForm.watch('bidMode') === 'strategy'

  const onStrategyInvalid = (
    formErrors: FieldErrors<IntradayStrategyFormValues>,
  ): void => {
    const alertMessage = resolveFormErrorAlertMessage(
      formErrors,
      t('page:biddingIntraday.serverError.validation'),
    )
    if (alertMessage !== undefined) {
      strategyForm.setError('root.serverError', { message: alertMessage })
    } else {
      strategyForm.clearErrors('root.serverError')
    }
  }

  const strategyValidationError =
    strategyForm.formState.errors.root?.serverError?.message

  return (
    <>
      <Modal
        footer={
          <VStack alignItems="stretch" gap={3} width="100%">
            {/* 保存・取消などの失敗はここ (ボタンの直上) に出す。 本文の先頭だと本文が
              スクロールしている時に画面外へ出て、 押しても何も起きないように見える
              (8080 の bem-error と同じ位置。 AC-BIN-058)。 */}
            {errorMessage !== undefined && (
              <Alert status="error" title={errorMessage} />
            )}
            <HStack justifyContent="flex-end" flexWrap="wrap" gap={2}>
              {/* 申請取消 (AC-BIN-048: 承認申請中の入札を申請者自身が下書きに戻す)。
              対象コマの GC 前限定で、 GC 後は AC-BIN-050 で拒否されるため無効化する。
              破壊的操作 (承認案件一覧から即時消滅) なので確認モーダルを挟む。 */}
              {approvalState === BiddingApprovalState.PENDING && (
                <TextButton
                  disabled={isBlocked}
                  loading={isTransitioning}
                  onClick={() => {
                    setShowCancelConfirm(true)
                  }}
                  size="sm"
                >
                  {t('page:biddingSpot.action.cancel')}
                </TextButton>
              )}

              {/* 承認 / 差戻し (AC-BIN-095)。 並びと button 種別はスポット入札の
              ApprovalStatusBanner と揃える: 申請取消 (申請者) → 差戻し → 承認 の順で、
              主操作の承認を右端に置く。 差戻しは「破壊的操作の前段導線」 なので
              OutlineDangerButton、 確定は RejectDialog 側の DangerButton。
              backend が承認・差戻しを受け付けないと返した札 (`approvalActionable` = false、
              GC 経過) と、 取得後に GC を跨いだ札 (isBlocked) は無効化する (AC-BIN-099)。 */}
              {approvalState === BiddingApprovalState.PENDING && (
                <>
                  <OutlineDangerButton
                    disabled={isBlocked || !approvalOrder.approvalActionable}
                    loading={isTransitioning}
                    onClick={() => {
                      approvalDecision.openReject(approvalOrder)
                    }}
                    size="sm"
                  >
                    {t('page:biddingIntraday.action.reject')}
                  </OutlineDangerButton>
                  <PrimaryButton
                    disabled={isBlocked || !approvalOrder.approvalActionable}
                    loading={isTransitioning}
                    onClick={() => {
                      approvalDecision.openApprove(approvalOrder)
                    }}
                    size="sm"
                  >
                    {t('page:biddingIntraday.action.approve')}
                  </PrimaryButton>
                </>
              )}

              {/* 差戻し後再編集 (AC-BIN-049)。 差戻された入札を同一 BidId のまま下書きに
              戻して再申請できるようにする唯一の操作なので、 spot の差戻しバナーと同じく
              PrimaryButton (issue #5147 の判断を踏襲)。 GC 経過後は無効化する
              (AC-BIN-049c)。 */}
              {approvalState === BiddingApprovalState.REJECTED && (
                <PrimaryButton
                  disabled={isBlocked}
                  loading={isTransitioning}
                  onClick={handleReopen}
                  size="sm"
                >
                  {t('page:biddingIntraday.action.reopen')}
                </PrimaryButton>
              )}

              {/* 取下 (AC-BIN-008 - intraday 専用、 spot では非表示)。 backend が取下できると
              返した札 (`withdrawable`) にだけ出す。 取得後に GC を跨いだ場合は isBlocked で
              押せなくする (表示の追従で、 出す側には働かない)。
              一覧の「取下」と同様に確認ダイアログを挟んでから WITHDRAW する。
              予約中 (isReserved) の予約取消 (AC-BIN-093) は一覧の行の取消ボタンで行い、
              フッターには出さない (機能詳細 6)。 */}
              {(entity ?? row).withdrawable && !isReserved && (
                <TertiaryButton
                  disabled={isBlocked}
                  loading={isTransitioning}
                  onClick={() => {
                    setShowWithdrawConfirm(true)
                  }}
                  size="sm"
                >
                  {t('page:biddingIntraday.action.withdraw')}
                </TertiaryButton>
              )}

              {/* AC-BIN-007 §機能詳細 6: フッターは「取消」(左)「キャンセル」「保存」(右)。
              保存操作は下書きだけに出し (それ以外は表示専用で「閉じる」)、 本文の編集
              フォームとは form 属性で結ぶ (footer は form の外にある)。 */}
              {isDraftEditable ? (
                <>
                  <OutlineDangerButton
                    disabled={isBlocked}
                    onClick={() =>
                      deleteConfirm.open(
                        convertIntradayBidFromOrder(
                          entity === undefined ? row : { ...row, ...entity },
                        ),
                      )
                    }
                    size="sm"
                  >
                    {t('page:biddingIntraday.action.delete')}
                  </OutlineDangerButton>
                  <TertiaryButton onClick={onClose} size="sm">
                    {t('common:action.cancel')}
                  </TertiaryButton>
                  <PrimaryButton
                    disabled={
                      isSaveBlocked ||
                      (isStrategyEditable && isBidLimitsLoading)
                    }
                    form={EDIT_FORM_ID}
                    loading={isSaving}
                    size="sm"
                    type="submit"
                  >
                    {t('common:action.save')}
                  </PrimaryButton>
                </>
              ) : (
                <TertiaryButton onClick={onClose} size="sm">
                  {t('common:action.close')}
                </TertiaryButton>
              )}
            </HStack>
          </VStack>
        }
        isOpen={isOpen}
        onClose={onClose}
        size="xl"
        title={`${t(
          isTitleEditable
            ? 'page:biddingIntraday.modal.editTitle'
            : 'page:biddingIntraday.modal.detailTitle',
        )} — ${formatProtoDate(
          row.targetDate,
        )} ${t('page:biddingIntraday.market.komaTitle', { koma: row.timeCode })}`}
      >
        <RegionLoading isLoading={isLoading}>
          {/* 詳細取得失敗はモーダル内 Alert で表示 (hook 側 throwOnError:false と対)。
            一覧行の情報 (row) は生きているので閉じて一覧に戻れる。 */}
          {fetchError !== null && (
            <Alert
              marginBottom={4}
              status="error"
              title={t('page:biddingIntraday.modal.detailLoadError')}
            />
          )}
          {/* AC-BIN-008b: 取消の失敗が確定した札は、 トーストを見ていなくても (閉じた・再読込した
            後でも) 分かるよう、 理由別の本文で先頭に残す。 札が終わった後 (全約定・未約定・失効) も出す。 */}
          {withdrawState === BiddingIntradayOrderWithdrawState.FAILED && (
            <Alert
              marginBottom={4}
              data-testid="withdraw-failed-alert"
              description={t(
                withdrawFailureTextKeys(withdrawFailureReason).description,
              )}
              status="error"
              title={t('page:biddingIntraday.withdrawFailure.title')}
            />
          )}
          {/* AC-BIN-095 / AC-BIN-049: 差戻し理由・日時は REJECTED に加え、 REOPEN 後の
            DRAFT (再編集中) と再申請後の PENDING でも表示保持する (スポット
            `ApprovalStatusBanner` と同じ分け方)。 見出しの文言は design/bidding/intraday.html
            の承認状態バー (差戻し / 下書きに戻した状態 / 再申請の前回差戻し) に合わせ、 REJECTED
            の見出しはスポットの AC-BSP-067 と同じ文。 error 色は現在差し戻されている REJECTED
            だけ。 backend は APPROVED 以降も rejected_* を消さないため、 それ以外の状態では
            出さない。 */}
          {rejectionNotice !== undefined && (
            <Alert
              marginBottom={4}
              description={
                hasRejectionHistory ? (
                  <>
                    {entity?.rejectedReason !== undefined && (
                      <Text>
                        {t(
                          'page:biddingIntraday.approvalAction.rejectedReason',
                        )}
                        : {entity.rejectedReason}
                      </Text>
                    )}
                    {entity?.rejectedAt !== undefined && (
                      <Text>
                        {t('page:biddingIntraday.approvalAction.rejectedAt')}:{' '}
                        {formatTimestamp(entity.rejectedAt)}
                      </Text>
                    )}
                  </>
                ) : undefined
              }
              status={rejectionNotice.status}
              title={rejectionNoticeTitle(rejectionNotice.kind, t)}
            />
          )}
          {/* AC-BIN-099 / AC-BIN-049c: なぜ承認 / 差戻し / 再編集が押せないのかを
            本文で伝える (ボタンは表示したまま無効化する)。 */}
          {(approvalState === BiddingApprovalState.PENDING ||
            approvalState === BiddingApprovalState.REJECTED) &&
            blockedReason !== undefined && (
              <Alert marginBottom={4} status="warning" title={blockedReason} />
            )}
          {/* AC-BIN-007: GC を過ぎた下書きはなぜ編集できないかを本文で伝える。 */}
          {isDraft && editBlockedReason !== undefined && (
            <Alert
              marginBottom={4}
              status="warning"
              title={editBlockedReason}
            />
          )}
          {/* AC-BIN-092: 予約中は表示専用で開き、 なぜ内容を変更できないか
            (予約取消してから再作成する運用) を本文で伝える。 */}
          {isReserved && (
            <Alert
              marginBottom={4}
              description={t(
                'page:biddingIntraday.reserved.editBlockedNoteBody',
              )}
              status="warning"
              title={t('page:biddingIntraday.reserved.editBlockedNoteTitle')}
            />
          )}
          {/* AC-BIN-098 (a) と後段: 承認者非該当 ・ 自己承認の常時警告。 操作口は disabled に
            しない。 ただし GC を過ぎた後 (blockedReason あり、 AC-BIN-099) は承認・差戻しが
            誰にもできず、 この注意は「押す前の忠告」 として役目を終えるので出さない
            (GC の Alert だけを残す。 alert-placement §3: 矛盾する Alert は閲覧者が
            できることに合うものだけ)。 AC-BIN-098 は GC 前の操作口に付ける注意を定める。 */}
          {approvalState === BiddingApprovalState.PENDING &&
            blockedReason === undefined &&
            approverNotices.map((notice) => (
              <Alert
                key={notice}
                marginBottom={4}
                status="warning"
                title={notice}
              />
            ))}
          {entity !== undefined && (
            <VStack align="stretch" gap={4}>
              <HStack gap={4}>
                <Box>
                  <Text color="Text.lowEmphasis" fontSize="xs">
                    {t('page:biddingIntraday.column.direction')}
                  </Text>
                  <TradeSideLabel side={entity.direction} />
                </Box>
                <Box>
                  <Text color="Text.lowEmphasis" fontSize="xs">
                    {t('page:biddingIntraday.column.bidStatus')}
                  </Text>
                  {/* 一覧と同じ 1 列の区分 (AC-BIN-035)。 承認済は遷移先の予約中 / 送信中で
                    表し (AC-BIN-051)、 送り直している札は送信中で出す (#10680)。 */}
                  <IntradayBidStatusCell bid={statusSource} />
                  <BiddingIntradayWithdrawStateTag
                    failureReason={entity.withdrawFailureReason}
                    state={entity.withdrawState}
                  />
                </Box>
              </HStack>

              {/* 多段階承認: 今待っている段と、 その段の承認者 (承認申請時の snapshot)。
                承認者非該当の警告 (上の Alert) と同じ currentStageApproverMemberIds。 */}
              {stageProgress !== undefined && (
                <VStack align="stretch" gap={1}>
                  <HStack gap={2}>
                    <Text color="Text.lowEmphasis" fontSize="xs">
                      {t(
                        'page:biddingIntraday.approvalAction.approvalStageLabel',
                      )}
                    </Text>
                    <Text>
                      {t(
                        'page:biddingIntraday.approvalAction.approvalStageProgress',
                        {
                          current: stageProgress.current,
                          total: stageProgress.total,
                        },
                      )}
                    </Text>
                  </HStack>
                  <HStack gap={2}>
                    <Text color="Text.lowEmphasis" fontSize="xs">
                      {t(
                        'page:biddingIntraday.approvalAction.approvalStageApprover',
                        { order: stageProgress.current },
                      )}
                    </Text>
                    <Text>
                      {approvalOrder.currentStageApproverMemberIds !==
                        undefined &&
                      approvalOrder.currentStageApproverMemberIds.length > 0
                        ? approvalOrder.currentStageApproverMemberIds
                            .map((id) => resolveDisplayName(id) ?? '—')
                            .join('、')
                        : t(
                            'page:biddingIntraday.approvalAction.notApproverNoApprovers',
                          )}
                    </Text>
                  </HStack>
                </VStack>
              )}

              <IntradayOrderEditForm
                entity={entity}
                feeState={feeState}
                formId={EDIT_FORM_ID}
                gcAtLabel={formatTimestamp(entity.gcAt)}
                isStrategyMode={isStrategyMode}
                onInvalidSubmit={setErrorMessage}
                onSaveBlockedChange={setIsSaveBlocked}
                onValidSubmit={(request) => {
                  void handleValidSave(request)
                }}
                readOnly={!isDraftEditable}
              />

              {/* AC-BIN-005: 保存したフォーミュラの入力値。 入札価格は保存時に計算した固定値。
                編集できる下書きは、 フォーミュラを使える間 (`INTRADAY_P8_AVAILABLE`) は編集フォームが
                保存済みの値で開くので、 表示専用の時だけ出す (使えない間は下書きを固定価格で開く)。 */}
              {entity.formula !== undefined && !isDraftEditable && (
                <Box>
                  <Text color="Text.lowEmphasis" fontSize="xs">
                    {t('page:biddingIntraday.form.priceTypeFormula')}
                  </Text>
                  <Text>{formulaSummary(t, entity.formula)}</Text>
                </Box>
              )}

              {workflowId !== undefined && workflowId !== '' && (
                <Box>
                  <Text color="Text.lowEmphasis" fontSize="xs">
                    {t('page:biddingIntraday.column.workflowId')}
                  </Text>
                  <Text>{workflowId}</Text>
                </Box>
              )}

              {/* 入札方式 / 戦略モードのスケジュール (AC-BIN-005b)。 下書きは編集、
                それ以外は表示専用 (AC-BIN-007)。 */}
              {deliveryDate !== undefined &&
                (isStrategyEditable ? (
                  <VStack align="stretch" gap={2}>
                    {strategyValidationError !== undefined && (
                      <Alert status="error" title={strategyValidationError} />
                    )}
                    <IntradayStrategyScheduleEditor
                      order={entity}
                      deliveryDate={deliveryDate}
                      form={strategyForm}
                      isMarketOrder={
                        entity.orderMethod ===
                        BiddingIntradayOrderOrderMethod.MARKET
                      }
                      koma={entity.timeCode}
                    />
                  </VStack>
                ) : (
                  <IntradayStrategyScheduleReadOnly
                    order={entity}
                    deliveryDate={deliveryDate}
                    koma={entity.timeCode}
                  />
                ))}

              <Separator />

              <HStack gap={8} color="Text.midEmphasis" fontSize="sm">
                <Box>
                  <Text fontWeight="bold">
                    {t('page:biddingIntraday.detail.createdAt')}
                  </Text>
                  <Text>{resolveDisplayName(entity.createdBy) ?? '—'}</Text>
                  <Text>{formatTimestamp(entity.createdAt)}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold">
                    {t('page:biddingIntraday.detail.updatedAt')}
                  </Text>
                  <Text>{resolveDisplayName(entity.updatedBy) ?? '—'}</Text>
                  <Text>{formatTimestamp(entity.updatedAt)}</Text>
                </Box>
              </HStack>
            </VStack>
          )}
        </RegionLoading>
      </Modal>

      {/* 申請取消確認 (AC-BIN-048)。 対象入札のサマリ (承認確認と同じ 1 行明細
        approvalDetailLine) と「申請取消すると承認案件が消滅します」 の警告を示し、
        確定した時だけ CANCEL を呼ぶ。 承認案件一覧からの即時消滅は取り返しが付かない
        (下書きの札自体は残るが、 承認フローのキュー・進行段階は失われる) ため、
        取下 (AC-008) と同じく破壊的操作として ConfirmDialog destructive を使う
        (SubmitConfirmModal の SaveCancelFooter は PrimaryButton 固定のため使わない)。 */}
      <ConfirmDialog
        body={
          <VStack align="stretch" gap={2}>
            <Text>{t('page:biddingIntraday.cancelRequest.body')}</Text>
            <Text>{approvalDetailLine}</Text>
          </VStack>
        }
        confirmLabel={t('page:biddingIntraday.cancelRequest.confirm')}
        destructive
        isLoading={isTransitioning}
        isOpen={showCancelConfirm}
        onCancel={() => {
          setShowCancelConfirm(false)
        }}
        onConfirm={() => {
          void handleCancelConfirm()
        }}
        title={t('page:biddingIntraday.cancelRequest.title')}
      />

      <IntradayDeleteConfirmDialog
        confirm={deleteConfirm}
        onDeleted={onClose}
      />

      {/* 取消確認 (AC-008): 一覧の「取下」と同じ確認ダイアログをモーダル内にも設ける。 */}
      <ConfirmDialog
        body={
          <>
            <p>{t('page:biddingIntraday.withdrawConfirm.body')}</p>
            <p>
              {formatWithdrawSummary(t, {
                timeCode: row.timeCode,
                areaCode: row.areaCode,
                direction: row.direction,
                orderMethod: row.orderMethod,
                priceYenPerKwh: row.priceYenPerKwh?.value,
                volumeMw: row.volumeMwh?.value,
              })}
            </p>
          </>
        }
        confirmLabel={t('page:biddingIntraday.withdrawConfirm.confirm')}
        destructive
        isLoading={isTransitioning}
        isOpen={showWithdrawConfirm}
        onCancel={() => {
          setShowWithdrawConfirm(false)
        }}
        onConfirm={() => {
          void handleWithdrawConfirm()
        }}
        title={t('page:biddingIntraday.withdrawConfirm.title')}
      />

      {/* 承認確認 (AC-BIN-097) + 差戻し (AC-BIN-095 / AC-BIN-053)。 組み立ては入札一覧の
        操作列 (BidCreateTab) と共通。 JEPX 制度値域 (AC-BIN-038) は開いた時点の
        client 側事前判定、 テナント入札制限 (AC-BIN-015) は APPROVE RPC の失敗からの抽出。
        売買逆転 (AC-BIN-045) は警告のみで承認を止めない。 */}
      <IntradayApprovalDialogs
        decision={approvalDecision}
        stageProgress={stageProgress}
      />
    </>
  )
}

function rejectionNoticeTitle(
  kind: IntradayRejectionNoticeKind,
  t: TFunction,
): string {
  switch (kind) {
    case 'rejected':
      return t('page:biddingIntraday.approvalAction.rejectedTitle')
    case 'reopenedDraft':
      return t('page:biddingIntraday.approvalAction.reopenedDraftTitle')
    case 'priorRejection':
      return t('page:biddingIntraday.approvalAction.priorRejectionTitle')
  }
}
